import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';

import { RrAlert, RrNav, type RrNavSection } from '@rr-crm/shared/components';

import { GlobalSearchBar } from './search/GlobalSearchBar';
import { useUserProfile } from './api/useUserProfile';
import { useAssistantEnabled, useAssistantStatus } from './assistant/useAssistant';
import { useResourcesEnabled, useResourcesStatus } from './resources/useResources';
import { canSeeAdminSettings } from './admin/adminTools';
import { ApprovalDetailErrorBoundary } from './approvals/ApprovalDetailErrorBoundary';
// OnboardingFlow is mounted in the app shell (rendered on every authenticated
// route, self-gating on hasCompletedOnboarding) — NOT a route — so it stays an
// eager import rather than joining the lazy route chunks below.
import { OnboardingFlow } from './onboarding/OnboardingFlow';
// T-VIEW-AS — persistent bottom banner; renders only while this tab impersonates.
import { ViewAsBanner } from './viewas/ViewAsBanner';
// IA-002 — client-side route access guard for admin/exec/countersign routes.
import { RequireRoles, ADMIN_ROUTE_ROLES, SUPERADMIN_ROUTE_ROLES } from './auth/RequireRoles';

/**
 * Phase 10B perf pass — route-level code-splitting.
 *
 * Every route component is loaded via `React.lazy` so each route ships as its
 * own chunk and the initial CRM bundle no longer eagerly pulls heavy, rarely
 * hit screens (QuoteBuilder ~1.5k LOC, SubmissionDetail, AccountDetail, and the
 * entire admin-tooling tree which only superAdmin/opsAdmin ever reach). The
 * components are named exports, so each `lazy()` maps the named export to the
 * `default` shape `React.lazy` expects.
 *
 * Kept eager (cheap, render-on-every-route): RrNav, GlobalSearchBar, the
 * adminTools helper, and the ApprovalDetail error boundary wrapper. Lazy
 * children are rendered under a single <Suspense> with a lightweight fallback.
 */
// RIG-363 — one role-aware tabbed Dashboard (My/Team/Executive). The former
// standalone RepDashboard / AdminDashboard / ExecutiveDashboard pages still
// exist (and are unit-tested) but are no longer routed directly — DashboardHome
// renders their bodies as tabs and /admin/dashboard /exec/dashboard redirect in.
const DashboardHome = lazy(() =>
  import('./dashboard/DashboardHome').then((m) => ({ default: m.DashboardHome })),
);
const AccountList = lazy(() =>
  import('./accounts/AccountList').then((m) => ({ default: m.AccountList })),
);
const AccountDetail = lazy(() =>
  import('./accounts/AccountDetail').then((m) => ({ default: m.AccountDetail })),
);
const ManualEntry = lazy(() =>
  import('./leads/ManualEntry').then((m) => ({ default: m.ManualEntry })),
);
const BulkImportLeads = lazy(() =>
  import('./imports/BulkImportLeads').then((m) => ({ default: m.BulkImportLeads })),
);
const HelpCenter = lazy(() => import('./help/HelpCenter').then((m) => ({ default: m.HelpCenter })));
const HelpArticle = lazy(() =>
  import('./help/HelpArticle').then((m) => ({ default: m.HelpArticle })),
);
const AdminSettings = lazy(() =>
  import('./admin/AdminSettings').then((m) => ({ default: m.AdminSettings })),
);
const PricingConfigAdmin = lazy(() =>
  import('./admin/PricingConfigAdmin').then((m) => ({ default: m.PricingConfigAdmin })),
);
const PricingConfigHistory = lazy(() =>
  import('./admin/PricingConfigHistory').then((m) => ({ default: m.PricingConfigHistory })),
);
const EmailTemplateAdmin = lazy(() =>
  import('./admin/EmailTemplateAdmin').then((m) => ({ default: m.EmailTemplateAdmin })),
);
const KnowledgeAdmin = lazy(() =>
  import('./admin/KnowledgeAdmin').then((m) => ({ default: m.KnowledgeAdmin })),
);
const RoleAssignmentsAdmin = lazy(() =>
  import('./admin/RoleAssignmentsAdmin').then((m) => ({ default: m.RoleAssignmentsAdmin })),
);
const AuditLogSearch = lazy(() =>
  import('./admin/AuditLogSearch').then((m) => ({ default: m.AuditLogSearch })),
);
const FounderListAdmin = lazy(() =>
  import('./admin/FounderListAdmin').then((m) => ({ default: m.FounderListAdmin })),
);
const AlertingConfigAdmin = lazy(() =>
  import('./admin/AlertingConfigAdmin').then((m) => ({ default: m.AlertingConfigAdmin })),
);
const QuickBooksIntegrationAdmin = lazy(() =>
  import('./admin/QuickBooksIntegrationAdmin').then((m) => ({
    default: m.QuickBooksIntegrationAdmin,
  })),
);
const ContractorsAdmin = lazy(() =>
  import('./contractors/ContractorsAdmin').then((m) => ({ default: m.ContractorsAdmin })),
);
const AccountMerge = lazy(() =>
  import('./admin/AccountMerge').then((m) => ({ default: m.AccountMerge })),
);
const AccountUnmerge = lazy(() =>
  import('./admin/AccountUnmerge').then((m) => ({ default: m.AccountUnmerge })),
);
const GdprPurge = lazy(() => import('./admin/GdprPurge').then((m) => ({ default: m.GdprPurge })));
// QA 2026-10-01 F-42 — restore list for deleted accounts (superAdmin).
const DeletedAccountsAdmin = lazy(() =>
  import('./admin/DeletedAccountsAdmin').then((m) => ({ default: m.DeletedAccountsAdmin })),
);
const QuoteBuilder = lazy(() =>
  import('./quotes/QuoteBuilder').then((m) => ({ default: m.QuoteBuilder })),
);
const NewProposalWorkflow = lazy(() =>
  import('./submissions/NewProposalWorkflow').then((m) => ({ default: m.NewProposalWorkflow })),
);
const SubmissionDetail = lazy(() =>
  import('./submissions/SubmissionDetail').then((m) => ({ default: m.SubmissionDetail })),
);
const PendingApprovalsList = lazy(() =>
  import('./approvals/PendingApprovalsList').then((m) => ({ default: m.PendingApprovalsList })),
);
const SignaturesQueue = lazy(() =>
  import('./signatures/SignaturesQueue').then((m) => ({ default: m.SignaturesQueue })),
);
const ApprovalDetail = lazy(() =>
  import('./approvals/ApprovalDetail').then((m) => ({ default: m.ApprovalDetail })),
);
const TaskListPage = lazy(() =>
  import('./tasks/TaskListPage').then((m) => ({ default: m.TaskListPage })),
);
const ScheduleMeetingPage = lazy(() =>
  import('./meetings/ScheduleMeetingPage').then((m) => ({ default: m.ScheduleMeetingPage })),
);
const MeetingDetailPage = lazy(() =>
  import('./meetings/MeetingDetailPage').then((m) => ({ default: m.MeetingDetailPage })),
);
const TrialDealDetail = lazy(() =>
  import('./deals/TrialDealDetail').then((m) => ({ default: m.TrialDealDetail })),
);
const ProfileSettings = lazy(() =>
  import('./settings/ProfileSettings').then((m) => ({ default: m.ProfileSettings })),
);
// Phase B (SO-001 commission visibility) — role-adaptive commission page.
// All staff see their own statements; admins see the team + a month-run control.
const CommissionPage = lazy(() =>
  import('./commission/CommissionPage').then((m) => ({ default: m.CommissionPage })),
);
const TokenSandbox = lazy(() =>
  import('@rr-crm/shared/components').then((m) => ({ default: m.TokenSandbox })),
);
// T-AI-RAG-003 — sales-engineer assistant chat.
const AssistantChat = lazy(() =>
  import('./assistant/AssistantChat').then((m) => ({ default: m.AssistantChat })),
);

// RIG-389 — render the assistant only where it is configured; otherwise redirect
// to the dashboard (the nav entry is hidden in those envs too, so this only
// catches direct-URL / bookmarked access). While the status is still loading we
// render nothing — avoiding both a premature redirect on staging and a 503 flash
// from the chat page on production.
function AssistantRoute(): JSX.Element | null {
  const { data: enabled, isPending } = useAssistantStatus();
  if (isPending) return null;
  return enabled ? <AssistantChat /> : <Navigate to="/dashboard" replace />;
}

// RIG-404 — Resources (the Sales Contractors SharePoint Resource Hub). Same
// fail-soft shape as the assistant: hidden from the nav AND redirected on
// direct access when the hub is not configured in this environment.
const ResourcesPage = lazy(() =>
  import('./resources/ResourcesPage').then((m) => ({ default: m.ResourcesPage })),
);
function ResourcesRoute(): JSX.Element | null {
  const { data: enabled, isPending } = useResourcesStatus();
  if (isPending) return null;
  return enabled ? <ResourcesPage /> : <Navigate to="/dashboard" replace />;
}

/**
 * Top-level CRM router.
 *
 * Routes wired here:
 *   - `/` — Rep dashboard (T2-017; replaces the prior placeholder home)
 *   - `/dashboard` — Alias for `/`; useful for explicit deep-linking
 *   - `/accounts` — Account list (T2-012)
 *   - `/accounts/:id` — Account detail (T2-011)
 *   - `/leads/new` — Manual lead entry form (T2-006)
 *   - `/imports/leads` — Bulk CSV lead import + per-row dedup confirm (T2-010B)
 *   - `/admin/pricing` — PricingConfig admin UI (T3-002; backend route layer
 *                       enforces superAdmin/opsAdmin auth — the page renders
 *                       and surfaces server-rejection 401/403 if a non-admin
 *                       hits the publish endpoint)
 *   - `/admin/pricing/history` — PricingConfig version history view (T3-003;
 *                       read-only; reuses /api/v1/pricing-config/history endpoint)
 */
/**
 * T4_6-004 nav section structure per Product UI Standard §5.1
 * desktop spec: items grouped under DM Mono UPPERCASE section labels.
 * The four sections (PIPELINE / RESOURCES / FINANCE / SYSTEM) come
 * directly from the Standard's worked example.
 *
 * "FINANCE" is empty for V1 (Invoice + commission UI lands in Phase 7
 * per §02 ledger); the section header is reserved so the navigation
 * layout is stable when those routes do ship.
 */
// PIPELINE / RESOURCES / FINANCE. Most items are role-independent, but a few
// are gated per RIG-248 ("a sales rep should only see menus they need"): an
// item with `roles` is shown ONLY to those roles; an item without `roles` is
// shown to all staff. `buildStaticSections(roles)` applies the filter and drops
// any section left empty. The SYSTEM section is computed separately — see
// `buildSystemSection`.
//
// Gated items (RIG-248): Approvals (the approver queue — only opsAdmin/superAdmin
// decide; viewAdmin may oversee) and Bulk import leads (an ops/bulk function) are
// hidden from salesRep + tech, who don't need them. Reps still REQUEST approvals
// from the deal/quote flow and create accounts via "New account".
interface GatedNavItem {
  id: string;
  label: string;
  to: string;
  /** Roles allowed to see this item. Omit = visible to all staff. */
  roles?: ReadonlyArray<string>;
  /**
   * RIG-389 — item is shown only when the assistant is configured in this
   * environment (Azure OpenAI wired). Production has no AOAI resource, so the
   * chat would always 503; hide the entry rather than surface a broken button.
   */
  requiresAssistant?: boolean;
  /**
   * RIG-404 — item is shown only when the Resource Hub (SharePoint) is
   * configured in this environment. Same fail-soft rule as the assistant.
   */
  requiresResources?: boolean;
}
interface GatedNavSection {
  title: string;
  items: ReadonlyArray<GatedNavItem>;
}

const ADMIN_NAV_ROLES = ['superAdmin', 'opsAdmin', 'viewAdmin'] as const;
const OPS_NAV_ROLES = ['superAdmin', 'opsAdmin'] as const;
// IA-014 — `tech` (maintenance owner) earns no commission, so the Commission
// nav showed it only its own empty ($0) statements — a dead-end. Hide it from
// tech. (Other roles see it: admins the team, salesRep their own.)
const NON_TECH_NAV_ROLES = ['superAdmin', 'opsAdmin', 'viewAdmin', 'salesRep'] as const;

const STATIC_NAV_SECTIONS: ReadonlyArray<GatedNavSection> = [
  {
    title: 'PIPELINE',
    items: [
      // RIG-363 — ONE role-aware "Dashboard" at the top of the nav (My / Team /
      // Executive tabs inside), replacing the three separate dashboard entries
      // (the old "My dashboard" here + "Admin dashboard"/"Executive dashboard"
      // in SYSTEM). Lands on /dashboard; the role's home tab is the default.
      { id: 'dashboard', label: 'Dashboard', to: '/dashboard' },
      { id: 'accounts', label: 'Accounts', to: '/accounts' },
      // RIG-363 — "New account" is no longer a standalone nav item; it's reached
      // via the "+ New account" CTA on the Accounts list + Dashboard (the route
      // /leads/new still resolves). This declutters the left menu.
      { id: 'tasks', label: 'Tasks', to: '/tasks' },
      // RIG-248 — approver QUEUE; hidden from salesRep/tech.
      //
      // RIG-484 read the narrower nav gate as an orphaned route, because the
      // backend list is `ALL_STAFF_ROLES`. It is not a defect: this menu item
      // is the approver's work queue, and a rep is not an approver. A rep
      // still reaches their own requests' detail pages from the dashboard
      // widget, which is the surface built for them. Left as RIG-248 designed
      // it deliberately, rather than widened to match an API gate that exists
      // so a requester can read their own row.
      { id: 'approvals', label: 'Approvals', to: '/approvals', roles: ADMIN_NAV_ROLES },
    ],
  },
  {
    // IA-008 — RESOURCES is now data-entry + tools: "Help" moved to SYSTEM (next
    // to "My profile") and "New account" moved to PIPELINE (IA-009). What remains
    // is the bulk/import data-entry tool (admins-only, IA-001/RIG-248) and the
    // T-AI-RAG-003 sales-engineer assistant (all-staff). For a salesRep the
    // admins-only import filters out, leaving just Assistant.
    // Reserved per the §5.1 Standard for future bulk-data tools.
    title: 'RESOURCES',
    items: [
      // RIG-248 — ops/bulk-CSV function; hidden from salesRep/tech.
      { id: 'imports-leads', label: 'Bulk import leads', to: '/imports/leads', roles: OPS_NAV_ROLES },
      // T-AI-RAG-003 — sales-engineer assistant. Visible to all staff, but only
      // where the assistant is configured (RIG-389: hidden when AOAI is unwired,
      // e.g. production, so we don't show a button that always errors).
      { id: 'assistant', label: 'Ask Howie', to: '/assistant', requiresAssistant: true },
      // RIG-404 — the Sales Contractors SharePoint Resource Hub, browsed and
      // downloaded from inside the CRM. Every staff role (Kenny's decision);
      // hidden where the hub is not configured so nobody sees a dead entry.
      { id: 'resources', label: 'Resources', to: '/resources', requiresResources: true },
    ],
  },
  {
    // Phase B — FINANCE section (previously reserved/empty per the §5.1 spec) is
    // activated by the SO-001 commission visibility surface. "Commission" is
    // scoped server-side: a salesRep sees their own statements, admins the team.
    // IA-010 — kept as a focused, intentionally-reserved section per the §5.1
    // Standard (NOT an oversight): Invoices + the rest of the finance surface
    // land here in Phase 7 per the §02 ledger. Documented so the single item
    // doesn't read as accidental.
    title: 'FINANCE',
    // IA-014 — hidden from tech (earns no commission → empty dead-end).
    items: [{ id: 'commission', label: 'Commission', to: '/commission', roles: NON_TECH_NAV_ROLES }],
  },
];

/**
 * RIG-248 — build the static nav sections filtered to the caller's roles: an
 * item with `roles` is shown only to those roles; an item without `roles` is
 * shown to all staff. Sections left empty after filtering are dropped. This is
 * a UX declutter (a sales rep sees only the menus they need); the backend route
 * gates remain the load-bearing access control.
 *
 * RIG-389 — `assistantEnabled` additionally drops any `requiresAssistant` item
 * (the "Ask Howie" entry) when the assistant is unconfigured in this env.
 * RIG-404 — `resourcesEnabled` does the same for the "Resources" entry.
 */
export function buildStaticSections(
  roles: string[] | undefined,
  assistantEnabled: boolean,
  resourcesEnabled = false,
): RrNavSection[] {
  const r = roles ?? [];
  return STATIC_NAV_SECTIONS.map((section) => ({
    title: section.title,
    items: section.items
      .filter((item) => item.roles === undefined || item.roles.some((role) => r.includes(role)))
      .filter((item) => !item.requiresAssistant || assistantEnabled)
      .filter((item) => !item.requiresResources || resourcesEnabled)
      .map(({ id, label, to }) => ({ id, label, to })),
  })).filter((section) => section.items.length > 0);
}

/**
 * T-ADMIN-SETTINGS-CONSOLIDATION — the SYSTEM nav section, computed from the
 * caller's roles so nav visibility mirrors access (defense-in-depth on top of
 * the load-bearing backend route gates). Replaces the prior static list of ~8
 * individual admin-tool links with ONE "Admin settings" entry that lands on the
 * role-gated hub.
 *
 *   - "Admin settings"      — superAdmin || opsAdmin (any admin tool visible)
 *   - "Admin dashboard"     — superAdmin || opsAdmin
 *   - "Executive dashboard" — superAdmin only
 *   - "My profile"          — all users
 *   - "Help"                — all users (IA-008: moved here from RESOURCES, which
 *                             mixed documentation with lead-creation actions; Help
 *                             is a support surface and reads better beside My profile)
 */
export function buildSystemSection(roles: string[] | undefined): RrNavSection {
  const items: RrNavSection['items'] = [];
  // RIG-363 — the Admin (Team) + Executive dashboards are no longer separate
  // SYSTEM entries; they're tabs of the single top-of-nav "Dashboard". SYSTEM
  // now holds only genuine system tools (Admin settings, My profile, Help).
  if (canSeeAdminSettings(roles)) {
    // Single consolidated entry → the role-gated Admin Settings hub. The hub +
    // each tool page render only the tools the role can use; the backend route
    // gates remain the load-bearing access control.
    items.push({ id: 'admin-settings', label: 'Admin settings', to: '/admin/settings' });
  }
  // "My profile" is available to every logged-in user.
  items.push({ id: 'settings-profile', label: 'My profile', to: '/settings/profile' });
  // IA-008 — Help Center, visible to ALL staff (the server filters article
  // content by audience vs the caller's roles). Lives in SYSTEM next to "My
  // profile" rather than RESOURCES, where it muddied the data-entry scent.
  items.push({ id: 'help', label: 'Help', to: '/help' });

  return { title: 'SYSTEM', items };
}

export function activeIdForPath(pathname: string): string | undefined {
  // RIG-363 — the unified Dashboard (My/Team/Executive tabs) plus the old
  // deep-link routes that redirect into it all keep the single "Dashboard" cue.
  if (
    pathname === '/' ||
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/admin/dashboard') ||
    pathname.startsWith('/exec/dashboard')
  )
    return 'dashboard';
  if (pathname.startsWith('/accounts')) return 'accounts';
  // IA-011 — Quote Builder + Deal detail are reached via an account; keep the
  // "you are here" cue on Accounts (matches the account-scoped /accounts/:id/
  // quotes/… variant, which already resolves to 'accounts' above).
  if (pathname.startsWith('/quotes') || pathname.startsWith('/deals')) return 'accounts';
  if (pathname.startsWith('/approvals')) return 'approvals';
  if (pathname.startsWith('/signatures')) return 'signatures';
  if (pathname.startsWith('/tasks')) return 'tasks';
  // RIG-363 — "New account" lost its standalone nav item; while creating one,
  // keep the Accounts entry highlighted (it's the parent surface + CTA host).
  if (pathname.startsWith('/leads/new')) return 'accounts';
  if (pathname.startsWith('/imports/leads')) return 'imports-leads';
  if (pathname.startsWith('/assistant')) return 'assistant';
  if (pathname.startsWith('/resources')) return 'resources';
  if (pathname.startsWith('/help')) return 'help';
  if (pathname.startsWith('/commission')) return 'commission';
  // T-ADMIN-SETTINGS-CONSOLIDATION — the hub AND every individual admin-tool
  // path resolve to 'admin-settings' so the consolidated nav entry stays
  // highlighted while the user is inside any admin tool (deep links still work).
  if (pathname.startsWith('/admin/settings')) return 'admin-settings';
  if (pathname.startsWith('/admin/pricing')) return 'admin-settings';
  if (pathname.startsWith('/admin/email-templates')) return 'admin-settings';
  if (pathname.startsWith('/admin/knowledge')) return 'admin-settings';
  if (pathname.startsWith('/admin/alerting')) return 'admin-settings';
  if (pathname.startsWith('/admin/founder-list')) return 'admin-settings';
  if (pathname.startsWith('/admin/role-assignments')) return 'admin-settings';
  if (pathname.startsWith('/admin/audit')) return 'admin-settings';
  if (pathname.startsWith('/admin/accounts/merge')) return 'admin-settings';
  if (pathname.startsWith('/admin/accounts/unmerge')) return 'admin-settings';
  if (pathname.startsWith('/admin/accounts/gdpr-purge')) return 'admin-settings';
  if (pathname.startsWith('/admin/accounts/deleted')) return 'admin-settings';
  if (pathname.startsWith('/admin/integrations')) return 'admin-settings';
  if (pathname.startsWith('/admin/contractors')) return 'admin-settings';
  if (pathname.startsWith('/settings/profile')) return 'settings-profile';
  return undefined;
}

function deriveInitials(name: string | undefined): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  const firstChar = first[0] ?? '';
  const lastChar = last[0] ?? '';
  return (firstChar + lastChar).toUpperCase();
}

export function App(): JSX.Element {
  const location = useLocation();
  const { accounts, instance } = useMsal();
  const activeAccount = accounts[0];
  const userInitials = deriveInitials(activeAccount?.name);
  const handleSignOut = () => {
    if (activeAccount) instance.logoutRedirect({ account: activeAccount }).catch(() => undefined);
  };

  // T-ADMIN-SETTINGS-CONSOLIDATION — the SYSTEM section is computed from the
  // user's resolved roles so the nav only surfaces admin entries to the roles
  // that can use them. PIPELINE / RESOURCES are role-independent.
  const {
    data: profile,
    isError: profileError,
    isPending: profilePending,
    refetch: refetchProfile,
  } = useUserProfile();
  // QA 2026-10-01 F-32: the menu is built from the roles, so until the
  // profile arrives the nav shows a skeleton instead of a guess. (It used to
  // render the role-less menu first and then grow, or shrink, a moment
  // later.) A failed load ends the skeleton: the role-free menu shows with
  // the retry banner below.
  const navLoading = profilePending && !profileError;
  // "Signatures" (the pending-countersignature queue) is a pipeline workflow
  // surface, but only superAdmin/opsAdmin countersign — so inject it into
  // PIPELINE only for those roles (nav visibility mirrors the backend gate).
  const isCountersigner = Boolean(
    profile?.roles?.some((r) => r === 'superAdmin' || r === 'opsAdmin'),
  );
  // RIG-389 — the assistant ("Ask Howie") is only surfaced where Azure OpenAI is
  // configured; in envs without it (e.g. production) the entry + route are hidden.
  const assistantEnabled = useAssistantEnabled();
  // RIG-404 — likewise, "Resources" only where the SharePoint Resource Hub is wired.
  const resourcesEnabled = useResourcesEnabled();
  const sections: RrNavSection[] = [
    // RIG-248 — static sections filtered to the caller's roles.
    ...buildStaticSections(profile?.roles, assistantEnabled, resourcesEnabled).map((section) =>
      section.title === 'PIPELINE' && isCountersigner
        ? {
            ...section,
            items: [...section.items, { id: 'signatures', label: 'Signatures', to: '/signatures' }],
          }
        : section,
    ),
    buildSystemSection(profile?.roles),
  ];

  return (
    <RrNav
      productName="CRM"
      sections={sections}
      loading={navLoading}
      activeItemId={activeIdForPath(location.pathname)}
      userInitials={userInitials}
      userRole={activeAccount?.username}
      onSignOut={handleSignOut}
      renderLink={(item, _isActive, children) => <Link to={item.to}>{children}</Link>}
    >
      {/* Phase 10C — first-use onboarding flow. Self-gates: renders nothing
          unless the loaded profile has hasCompletedOnboarding=false. Mounted
          inside the shell so it overlays the app on first login. */}
      <OnboardingFlow />
      {/* T-VIEW-AS — persistent bottom banner while impersonating (per-tab). */}
      <ViewAsBanner />
      {/* IA-006 — the nav is derived from useUserProfile(); if that fetch fails,
          the role-gated entries (admin tools, Signatures) silently disappear.
          Surface it explicitly with a retry instead of degrading silently. */}
      {profileError && (
        <div className="px-4 py-2">
          <RrAlert variant="warning">
            Some menus may be missing. We couldn’t load your account permissions.{' '}
            <button
              type="button"
              onClick={() => void refetchProfile()}
              className="font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </RrAlert>
        </div>
      )}
      {/* T9-017 — Global search ribbon. Sticky just below the nav chrome on
          mobile and at the top of the main content column on desktop. */}
      <div className="sticky top-0 z-20 bg-rr-page px-4 py-2 border-b border-rr-border-default">
        <GlobalSearchBar />
      </div>
      {/* Phase 10B — single Suspense boundary for the lazily-loaded route
          chunks. The fallback is a lightweight placeholder shown only during
          the (cached-after-first-visit) chunk fetch. */}
      <Suspense
        fallback={
          <div className="px-4 py-8 text-sm text-rr-fg-secondary" role="status" aria-live="polite">
            Loading…
          </div>
        }
      >
      <Routes>
        {/* T9-020 — Rep dashboard replaces the Phase-2 stub at `/` and
            `/dashboard`. The legacy `Dashboard` is kept exported and
            mounted at `/dashboard-legacy` for fallback during the
            Phase 9 rollout window (it can be removed in a Phase 10A
            cleanup pass). */}
        {/* RIG-363 — one role-aware tabbed Dashboard. `/` and `/dashboard`
            land on the role's home tab; `/dashboard/:tab` deep-links a tab.
            DashboardHome gates tab visibility by role (the bodies' backend
            routes remain the load-bearing access control). */}
        <Route path="/" element={<DashboardHome />} />
        <Route path="/dashboard" element={<DashboardHome />} />
        <Route path="/dashboard/:tab" element={<DashboardHome />} />
        {/* RIG-484 — `/dashboard-legacy` and its 283-line `Dashboard` page are
            gone. Zero inbound links anywhere in the app, and the route's own
            comment said it "can be removed in a Phase 10A cleanup pass" —
            which never happened, so it sat there as a second, diverging
            dashboard reachable only by typing the URL. */}
        {/* RIG-363 — old deep-link routes redirect into the matching tab so
            existing bookmarks survive. */}
        <Route path="/admin/dashboard" element={<Navigate to="/dashboard/team" replace />} />
        <Route path="/exec/dashboard" element={<Navigate to="/dashboard/executive" replace />} />
        <Route path="/accounts" element={<AccountList />} />
        <Route path="/accounts/:id" element={<AccountDetail />} />
        {/* T10A-003 — deep-linkable account-detail tabs. `/accounts/:id/:tab`
            renders the account detail page with the named tab active (e.g.
            `/accounts/ACC-123/submissions`). AccountDetail validates the tab
            segment against its whitelist: QA 2026-10-01 F-18 redirects
            `quotes` / `proposals` to the Proposals tab and shows a not-found
            state for any other unknown value (never a blank page, never a
            silent Overview). The more-specific nested
            routes below (`submissions/new`, `submissions/:id`, `quotes/new`,
            `quotes/:id`, `meetings/new`, `meetings/:id`) outrank this route
            under React Router v6 ranked matching, so they are not shadowed. */}
        <Route path="/accounts/:id/:tab" element={<AccountDetail />} />
        <Route path="/leads/new" element={<ManualEntry />} />
        <Route path="/imports/leads" element={<BulkImportLeads />} />
        {/* T10C-002/003 — Help surface. /help (list + search) and
            /help/:slug (detail). Visible to all staff; server filters by
            audience. :slug is placed after the static /help so it doesn't
            shadow the list route. */}
        <Route path="/assistant" element={<AssistantRoute />} />
        <Route path="/resources" element={<ResourcesRoute />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/help/:slug" element={<HelpArticle />} />
        {/* T-ADMIN-SETTINGS-CONSOLIDATION — Admin Settings hub (role-gated
            landing page linking to each existing admin tool; the tools keep
            their own routes below so deep links still work). */}
        <Route path="/admin/settings" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><AdminSettings /></RequireRoles>} />
        <Route path="/admin/pricing" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><PricingConfigAdmin /></RequireRoles>} />
        <Route path="/admin/pricing/history" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><PricingConfigHistory /></RequireRoles>} />
        {/* T10A-EMAILTPL — EmailTemplate admin (superAdmin/opsAdmin; the page
            gates its own visibility, the backend requireAnyRole is load-bearing). */}
        <Route path="/admin/email-templates" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><EmailTemplateAdmin /></RequireRoles>} />
        <Route path="/admin/knowledge" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><KnowledgeAdmin /></RequireRoles>} />
        {/* T10A-ROLEADMIN — role-assignment list + history + reconcile (superAdmin). */}
        <Route path="/admin/role-assignments" element={<RequireRoles roles={SUPERADMIN_ROUTE_ROLES}><RoleAssignmentsAdmin /></RequireRoles>} />
        <Route path="/admin/audit" element={<RequireRoles roles={SUPERADMIN_ROUTE_ROLES}><AuditLogSearch /></RequireRoles>} />
        <Route path="/admin/founder-list" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><FounderListAdmin /></RequireRoles>} />
        {/* T10A-ALERTING — alerting thresholds + recipient rules editor
            (superAdmin/opsAdmin; the page gates its own visibility, the
            backend requireAnyRole gate is load-bearing). */}
        <Route path="/admin/alerting" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><AlertingConfigAdmin /></RequireRoles>} />
        {/* T7-003-UI — QuickBooks Online integration (OAuth connect/disconnect +
            status; superAdmin/opsAdmin — the page gates its own visibility, the
            backend requireAnyRole gate is load-bearing). The backend OAuth
            callback redirects here with ?status=connected. */}
        <Route
          path="/admin/integrations/quickbooks"
          element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><QuickBooksIntegrationAdmin /></RequireRoles>}
        />
        {/* Contractors — commission-earning contractor management (superAdmin/
            opsAdmin; the page gates its own visibility, backend requireAnyRole
            is load-bearing). */}
        <Route path="/admin/contractors" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><ContractorsAdmin /></RequireRoles>} />
        {/* T10A-MERGE — Account merge (Tier 3.5; destructive; superAdmin). */}
        <Route path="/admin/accounts/merge" element={<RequireRoles roles={SUPERADMIN_ROUTE_ROLES}><AccountMerge /></RequireRoles>} />
        {/* T-ACCOUNT-MERGE-UNDO — Account unmerge (Tier 3.5; destructive; superAdmin). */}
        <Route path="/admin/accounts/unmerge" element={<RequireRoles roles={SUPERADMIN_ROUTE_ROLES}><AccountUnmerge /></RequireRoles>} />
        {/* T10B-010 — GDPR/CCPA right-to-erasure PURGE (Tier 3.5; DESTRUCTIVE +
            IRREVERSIBLE; superAdmin; the page gates its own visibility). */}
        <Route path="/admin/accounts/gdpr-purge" element={<RequireRoles roles={SUPERADMIN_ROUTE_ROLES}><GdprPurge /></RequireRoles>} />
        {/* QA 2026-10-01 F-42 — Deleted accounts + Restore (superAdmin). */}
        <Route path="/admin/accounts/deleted" element={<RequireRoles roles={SUPERADMIN_ROUTE_ROLES}><DeletedAccountsAdmin /></RequireRoles>} />
        <Route path="/quotes/new" element={<QuoteBuilder />} />
        <Route path="/quotes/:id" element={<QuoteBuilder />} />
        {/* T3-014M — account-scoped quote routes (preferred over /quotes/:id);
            QuoteBuilder reads :accountId param to pre-fill the account field
            for new quotes; existing :id param is preserved for the quote id. */}
        <Route path="/accounts/:accountId/quotes/new" element={<QuoteBuilder />} />
        <Route path="/accounts/:accountId/quotes/:id" element={<QuoteBuilder />} />
        {/* T5-004 — Submissions surface (Option 1 IA-doctrine scope).
            NewProposalWorkflow is the Step-1 build page; SubmissionDetail
            is the Step-2 preview/edit/confirm page (state-gated panel). */}
        <Route
          path="/accounts/:accountId/submissions/new"
          element={<NewProposalWorkflow />}
        />
        <Route
          path="/accounts/:accountId/submissions/:id"
          element={<SubmissionDetail />}
        />
        {/* T4-003 — Pending approvals queue (opsAdmin / superAdmin land via
            Dashboard AdminLinks; all staff can view the route directly).
            Per-type detail views ship in T4-004 (pilot — landed) /
            T4-005 (additional_discount) / T4-006 (non_standard_terms) /
            T4-012 (trial). */}
        <Route path="/approvals" element={<PendingApprovalsList />} />
        <Route path="/signatures" element={<RequireRoles roles={ADMIN_ROUTE_ROLES}><SignaturesQueue /></RequireRoles>} />
        {/* T10A-001 — route-scoped error boundary so a render error in the
            approval detail tree shows a graceful fallback with a Back to
            Approvals affordance instead of a white-screen dead-end. */}
        <Route
          path="/approvals/:id"
          element={
            <ApprovalDetailErrorBoundary>
              <ApprovalDetail />
            </ApprovalDetailErrorBoundary>
          }
        />
        {/* T9-005 — Task list page */}
        <Route path="/tasks" element={<TaskListPage />} />
        {/* T9-008 / T9-010 — Meeting scheduling + detail */}
        <Route
          path="/accounts/:accountId/meetings/new"
          element={<ScheduleMeetingPage />}
        />
        <Route
          path="/accounts/:accountId/meetings/:id"
          element={<MeetingDetailPage />}
        />
        {/* T4-013e — Trial Deal detail surface + Convert to paid. Phase 5+
            extends to handle non-trial Deals. */}
        <Route path="/deals/:id" element={<TrialDealDetail />} />
        <Route path="/settings/profile" element={<ProfileSettings />} />
        {/* Phase B — SO-001 commission visibility (all staff; backend scopes
            non-admins to their own statements). */}
        <Route path="/commission" element={<CommissionPage />} />
        {/* T4_6-002 — Product UI Standard token sandbox. Internal-only;
            no nav surface. Walk-only path Kenny visits to verify the
            Phase 4.6 token foundation. */}
        <Route path="/_design/tokens" element={<TokenSandbox />} />
        {/* M21 — catch-all 404. Any unmatched URL previously rendered nav
            chrome + an empty main (a silent dead-end, and the failure mode
            behind C1's broken /leads/import link). */}
        <Route
          path="*"
          element={
            <main className="mx-auto max-w-2xl px-4 py-12 text-center">
              <h1 className="text-lg font-semibold text-rr-fg-primary">Page not found</h1>
              <p className="mt-2 text-sm text-rr-fg-secondary">
                That page doesn&rsquo;t exist or may have moved.
              </p>
              <Link to="/" className="mt-4 inline-block text-sm text-rr-link underline">
                Go to your dashboard
              </Link>
            </main>
          }
        />
      </Routes>
      </Suspense>
    </RrNav>
  );
}
